import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
// TM-1000002003: both price dialogs of THIS shell route their X and Escape into the mounted
// wizard's own close guard, so this host needs a handle on each run.
import {
    CmPriceChangeWizardComponent
} from '../../../../../object-components/cm-price-change-wizard/cm-price-change-wizard.component';
import {
    CmPaymentPauseWizardComponent
} from '../../../../../object-components/cm-payment-pause-wizard/cm-payment-pause-wizard.component';

import {MvsObjectNavigationService} from '../../../../../../../../core/shared/navigation/mvs-object-navigation.service';
import {MvsCoreService} from '../../../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../../../core/shared/service/message/mvs-message.service';
import {MvsConfigService} from '../../../../../../../../core/shared/config/mvs-config.service';

import {CustomerContractService} from '../../../../../../../cr/service/api/customer-contract.service';
import {ObjectTypeService} from '../../../../../../../../core/cc/service/api/object-type.service';

import {WidgetData} from '../../../../../../../../core/shared/widget/widget-data';
import {WidgetHelperButton} from '../../../../../../../../core/shared/basic/widget-helper-button';
import {ObjectIdentifierData} from '../../../../../../../../core/shared/basic/object-identifier-data';
import {MvsObjectNavigationEntry} from '../../../../../../../../core/shared/navigation/mvs-object-navigation-entry';
import {
    MvsObjectNavigationActionEnum
} from '../../../../../../../../core/shared/navigation/mvs-object-navigation-action-enum';
import {ObjectChangeInformation} from '../../../../../../../../core/shared/logic/object-change-information';
import {
    CmContractStandardNavigationComponent
} from "../../../../cm-contract/components/cm-contract-standard-navigation/cm-contract-standard-navigation.component";
import {TicketService} from "../../../../../../../../feature-core/tm/service/api/ticket.service";
import {ObjectIdentifier} from "../../../../../../../../core/shared/basic/object-identifier";
import {DtoImportObjectContext} from '../../../../../../../../core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from '../../../../../../../../core/shared/dto/dto.import.object.context.entry';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Consultant shell of the contract page's „Standard“ navigation — its template hosts the
 *   GENERIC form (mvs-crud-object) of cm.ContractPaymentSuspension in the „Beitragsaussetzung
 *   bearbeiten“ dialog: update mode only, generic delete while not reported, start, end and reason
 *   read-only and no delete once reported (V10, D22). The change handler (which evaluates the event
 *   action) and the dialog state are INHERITED from CmContractStandardNavigationComponent.
 *
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: The widgets of this shell, including the „Beitragsaussetzungen“ QL table (dataSource "ql" /
 *   dataProvider "list", root cm.ContractPaymentSuspension, fixed to the current contract), are
 *   configured by the inherited refreshWidgets() under the ':co' alias suffix.
 *
 * ONE comment block on purpose: the CRUD checker (check-crud.js G001–G005) and the data-provider
 * checker (check-dp.js G001–G004) both read only the FIRST AI block of a file.
 */
@Component({
    selector: 'cm-contract-standard-navigation-consultant',
    templateUrl: './cm-contract-standard-navigation-consultant.component.html',
    standalone: false
})
export class CmContractStandardNavigationConsultantComponent
    extends CmContractStandardNavigationComponent
    implements OnInit, OnChanges, OnDestroy {

    // Consultant-only properties
    disableUpdate: boolean = true;
    contractLabelWidget: WidgetData;

    /**
     * Visibility of the "Preisänderung" dialog that hosts {@code cm-price-change-wizard}.
     *
     * DECLARED HERE, not inherited. The standard shell moved both price wizards into the contract
     * header's "Quick Actions" menu ({@code cm-contract-quick-actions}), so the base class no longer
     * carries these flags — but the consultant shell keeps opening them from its own "Laufende
     * Beiträge" toolbar and this template still binds them.
     *
     * TM-1000002003: bound ONE-WAY on the dialog ([visible], not [(visible)]). The only writer that
     * may lower it is {@link #handlePriceChangeWizardClose} — see the template for the bypass that
     * cost, and {@link #requestPriceChangeWizardClose} for why this shell had to be fixed too.
     *
     * @ticket TM-1000001998
     */
    dialogPriceChangeWizard: boolean = false;

    /**
     * The mounted "Preisänderung" run, or undefined while the dialog is closed.
     *
     * TM-1000002003: a ViewChild and NOT an output of the wizard, because the direction is wrong for
     * an output — the X and Escape live in THIS component's template, so the host has to be able to
     * INITIATE the close request; a wizard-raised event could only report one. Non-static: the run
     * only exists behind the dialog's *ngIf, and the whole template sits behind
     * {@code *ngIf="initialized"}.
     */
    @ViewChild(CmPriceChangeWizardComponent) priceChangeWizard: CmPriceChangeWizardComponent;

    /**
     * Visibility of the "Beitrag aussetzen" dialog that hosts {@code cm-payment-pause-wizard}.
     * Same reason for living here as {@link #dialogPriceChangeWizard}.
     *
     * TM-1000002003: also bound ONE-WAY; only {@link #handlePaymentPauseWizardClose} may lower it.
     *
     * @ticket TM-1000001998
     */
    dialogPaymentPauseWizard: boolean = false;

    /** The mounted "Beitrag aussetzen" run, or undefined while the dialog is closed. */
    @ViewChild(CmPaymentPauseWizardComponent) paymentPauseWizard: CmPaymentPauseWizardComponent;

    // ---- Lifecycle ----
    override ngOnInit(): void {
        this.setAliasSuffix(':co')
        this.initComponent();
    }

    // Kept in step with the base implementation: "dto" must trigger a refresh too, because the
    // parent replaces the contract dto IN PLACE on an object refresh and the derived toolbar state
    // is read from it. Diverging here would silently leave the consultant shell on stale data.
    override ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) return;
        if (changes['objectIdentifier'] || changes['dto']) this.refreshComponent();
    }

    override ngOnDestroy(): void {
        // The consultant shell adds no subscriptions of its own, but the base class DOES own some —
        // among them the status-relevant change subscription (price slices AND payment suspensions)
        // and the payment-suspension access check, both installed by initComponent(), which this
        // class inherits and therefore also has to tear down. The previous body was empty on the
        // assumption that "the base class already handles its own cleanup"; overriding without
        // calling super is exactly what prevents that, so the base teardown is invoked explicitly.
        super.ngOnDestroy();
    }

    // IMPORTANT: do NOT call super.refreshComponent() to avoid base dtoImportObjectContext, etc.
    override refreshComponent(): void {
        this.refreshWidgets();      // build consultant widgets only

        // The Preis-Assistent resolves its cm.Contract from the import object context, so the
        // consultant shell has to provide that one entry. Deliberately narrower than the base
        // implementation, which additionally carries the cr.Customer of the contract.
        this.dtoImportObjectContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('cm.Contract', this.objectIdentifier.objectId)]
        );

        this.initialized = true;
    }

    constructor(
        protected ticketService: TicketService,
        protected override navigationService: MvsObjectNavigationService,
        protected override coreService: MvsCoreService,
        protected override confirmationService: ConfirmationService,
        protected override messageService: MvsMessageService,
        protected override customerContractService: CustomerContractService,
        protected override objectTypeService: ObjectTypeService,
        protected override configService: MvsConfigService
    ) {
        super(
            ticketService,
            navigationService,
            coreService,
            confirmationService,
            messageService,
            customerContractService,
            objectTypeService,
            configService
        );
    }

    // ---- Consultant widgets (full override; do NOT call super.refreshWidgets()) ----
    override refreshWidgets(): void {
        super.refreshWidgets();

    }

    // Consultant keeps this behavior
    override refreshContractStatusWidget(): void {
        super.refreshContractStatusWidget();
    }
    override refreshTicketWidget(): void {
        super.refreshTicketWidget();
    }

    // Consultant-only handler present in your original code
    handleContractStatusAdd(object: ObjectChangeInformation) {
        this.refreshContractStatusWidget();
    }

    // Keep ticket navigation behavior identical
    override handleTicketSelect(event: ObjectIdentifierData | ObjectIdentifier) {
        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            'tm.Ticket', event.objectId, null, 'Object', null, null, MvsObjectNavigationActionEnum.any
        );
        this.navigationService.navigateTo(entry, 'right');
    }

    // ---- Price wizards (CONSULTANT SHELL ONLY) ----
    //
    // The three members below used to live in the base class. They moved down here when the standard
    // shell's price actions moved UP into the contract header ({@code cm-contract-quick-actions}):
    // the header component is the only place that can offer them on every tab, and the base class is
    // no longer involved in opening either wizard. The consultant shell has no such header, so it
    // keeps the original toolbar entry point — the button array {@code priceToolbarButtons} is still
    // built by the inherited {@code refreshWidgets()} and bound by this component's own template.
    //
    // @ticket TM-1000001998

    /**
     * Handle a toolbar button click of the "Laufende Beiträge" widget. Each of the two price wizards
     * carries its own action id, assigned where the buttons are created
     * ({@code CmContractStandardNavigationComponent#refreshWidgets}).
     *
     * @ticket TM-1000001998
     */
    handlePriceToolbarClick(button: WidgetHelperButton): void {

        if (button.action === 'open-price-change-wizard') {
            this.dialogPriceChangeWizard = true;
            return;
        }

        if (button.action === 'open-payment-pause-wizard') {
            this.dialogPaymentPauseWizard = true;
        }
    }

    /**
     * The "Preisänderung" wizard closed — close the dialog AND reload the widgets so the newly
     * created price slices appear in "Laufende Beiträge".
     *
     * The refresh is REQUIRED, not cosmetic: without it the user lands back on a contract page still
     * showing the pre-change price list and has to reload by hand.
     *
     * No success toast is raised here — mvs-crud-object-generic already reports each create and the
     * wizard's own "Abschluss" screen is the wizard specific feedback, so a third message would
     * double-toast.
     *
     * TM-1000002003 — THIS IS THE FUNNEL, and now the only one. Every exit of the dialog ends here:
     * the wizard's own "Abschluss" button, the header X and Escape (both via
     * {@link #requestPriceChangeWizardClose} -> the wizard's close guard -> its onDialogClose).
     * Before the funnel the X and Escape lowered the two-way bound visible flag themselves, so this
     * method never ran on those paths: the "Laufende Beiträge" widget was never reloaded after a
     * written price change, and a half-finished run was discarded without a word — in THIS shell,
     * for the very same wizard the standard shell already protects.
     *
     * NOTE: the reload here is {@code refreshWidgets()}, not a {@code contractChanged} emitter. This
     * component has no such output and needs none — it owns the widgets itself. No emitter was
     * invented for a consumer that does not exist.
     *
     * @ticket TM-1000001998
     */
    handlePriceChangeWizardClose(): void {
        this.dialogPriceChangeWizard = false;
        this.refreshWidgets();
    }

    /**
     * TM-1000002003: an ACCIDENTAL exit of the Preisänderung dialog was requested — the header X or
     * Escape. Handed to the mounted run so its own close guard decides; this component never lowers
     * the flag itself.
     *
     * WHY THIS SHELL NEEDED THE SAME TREATMENT: a guard one shell honours and another ignores is
     * worse than no guard, because the clerk learns to trust it. The consultant shell opens the SAME
     * two wizards from its "Laufende Beiträge" toolbar, so leaving its dialogs on PrimeNG's own X and
     * Escape kept a live bypass of the confirmation the standard shell raises.
     *
     * ESCAPE: routed here by a plain (keydown.escape) binding on the <p-dialog> element rather than
     * by PrimeNG's closeOnEscape, which is off precisely because it dismisses without asking. The
     * binding works because the dialog renders IN PLACE — verified for this shell rather than
     * assumed: PrimeNG 20.4.0 computes {@code $appendTo = appendTo() || config.overlayAppendTo()},
     * {@code overlayAppendTo} is a signal defaulting to 'self' (primeng/config), the wrapper is only
     * relocated {@code if ($appendTo() && $appendTo() !== 'self')} (primeng/dialog), neither dialog
     * in this template sets [appendTo], and shell-consultant's own {@code providePrimeNG} passes only
     * theme, ripple and locale — no overlayAppendTo override. So the wrapper stays inside this
     * component's DOM and a keydown inside the dialog bubbles to this element. Should that default
     * ever change under us the binding simply stops firing and Escape does nothing — the fallback is
     * a DEAD key, never a silent discard.
     *
     * The wizard being absent means the dialog is open with no run mounted (nothing to protect), so
     * that case closes through the funnel directly instead of getting stuck.
     *
     * @ticket TM-1000002003
     */
    requestPriceChangeWizardClose(): void {

        if (!this.priceChangeWizard) {
            this.handlePriceChangeWizardClose();
            return;
        }

        this.priceChangeWizard.requestClose();
    }

    /**
     * The "Beitrag aussetzen" wizard closed. Same contract as
     * {@link #handlePriceChangeWizardClose}: close and reload the price list, raise no toast.
     *
     * Kept as a SEPARATE handler rather than one shared method so each dialog owns exactly one flag —
     * a single handler resetting both would close the other dialog too if they were ever opened in
     * sequence without a change-detection pass in between.
     *
     * TM-1000002003 — THE FUNNEL, same contract as {@link #handlePriceChangeWizardClose}, and the
     * same bypass closed: before it, PrimeNG's X and Escape lowered the two-way bound flag and this
     * method never ran, so a written Beitragsaussetzung left the "Laufende Beiträge" widget showing
     * the pre-pause price list.
     *
     * @ticket TM-1000001998
     */
    handlePaymentPauseWizardClose(): void {
        this.dialogPaymentPauseWizard = false;
        this.refreshWidgets();
    }

    /**
     * TM-1000002003: an ACCIDENTAL exit of the "Beitrag aussetzen" dialog was requested — the header
     * X or Escape. Handed to the mounted run so its own close guard decides.
     *
     * Same Escape mechanics and the same in-place verification as
     * {@link #requestPriceChangeWizardClose}.
     *
     * @ticket TM-1000002003
     */
    requestPaymentPauseWizardClose(): void {

        if (!this.paymentPauseWizard) {
            this.handlePaymentPauseWizardClose();
            return;
        }

        this.paymentPauseWizard.requestClose();
    }
}
